import React, { forwardRef, useEffect, useRef } from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import FormCheck, { FormCheckProps } from 'react-bootstrap/FormCheck'
import {
  ResourceWithAuthz,
  useHasRequiredCapabilities,
} from 'src/app/AppAuth/AppAuth.hooks'
import palette from 'src/designSystem/variables/palette'
import styled from 'styled-components/macro'
import { useId } from 'src/hooks/useId'

export const StyledCheckInput = styled(FormCheck)`
  &.form-check {
    display: flex;
    align-items: center;
    padding: 0;

    &.form-check-inline {
      display: inline-flex;
    }

    .form-check-input {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      margin: 0;
      margin-right: 8px;

      &:checked {
        border: none;
        background-color: ${palette.neutral['800']};
        color: ${palette.neutral.white};
        &:hover,
        &:focus {
          box-shadow: 0 0 0 0.25rem ${palette.neutral['100']};
        }
      }

      &:indeterminate {
        background-color: ${palette.neutral['300']};
        border-color: ${palette.neutral['300']};
      }
    }

    .form-check-label {
      flex-grow: 1;
    }
  }
`

// Workaround for React 18 compatibility issues https://github.com/styled-components/styled-components/issues/3738
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CheckInputProxy: any = StyledCheckInput

type CheckInputProps = {
  requiredCapabilities?: CAPABILITY[]
  resource?: ResourceWithAuthz
  indeterminate?: boolean
} & Omit<FormCheckProps, 'type'>

/**
 * ! IMPORTANT DETAILS FOR THE `onChange` event handler
 * - DO NOT call the event's `preventDefault` method in the handler
 * - Doing so will cause issues when clicking the label
 *   - It may not properly check / uncheck the input
 */
export const CheckBoxInput = forwardRef<HTMLInputElement, CheckInputProps>(
  ({ indeterminate, ...props }, ref) => {
    const hasRequiredPermissions = useHasRequiredCapabilities(
      props.requiredCapabilities,
      props.resource,
    )

    const id = useId(props.id)
    const wrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (wrapperRef.current) {
        const input = wrapperRef.current.querySelector('input[type="checkbox"]')
        if (input instanceof HTMLInputElement) {
          input.indeterminate = !!indeterminate
        }
      }
    }, [indeterminate])

    return (
      <div ref={wrapperRef} style={{ display: 'contents' }}>
        <CheckInputProxy
          ref={ref}
          {...props}
          id={id}
          disabled={!hasRequiredPermissions || props.disabled}
        />
      </div>
    )
  },
)

export const CheckBoxInputPublic = forwardRef<
  HTMLInputElement,
  CheckInputProps
>((props, ref) => {
  const id = useId(props.id)
  return (
    <CheckInputProxy ref={ref} {...props} id={id} disabled={props.disabled} />
  )
})
